import { useEffect, useMemo, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { useEncryptedParams } from "../../hooks/useEncryptedParams";
import { encrypt } from "../../utils/encryption";
import { useMasterModals } from "../../hooks/useMasterModals";
import apiClient from "../../services/apiClient";
import { normalizeApiError } from "../../utils/apiError";
import Button from "../../components/common/Button";
import CrudForm from "../../components/common/crud/CrudForm";
import ErrorAlert from "../../components/common/ErrorAlert";
import CrudTable from "../../components/common/crud/CrudTable";
import useCrud from "../../components/common/crud/useCrud";
import countryService from "../../services/countryService";
import alertService from "../../utils/alertService";

const vendorFormSchema = [
  {
    name: "vendorName",
    label: "Vendor Name",
    type: "text",
    placeholder: "ABC Metals Pvt Ltd",
    validation: {
      required: true,
      minLength: 3,
      maxLength: 100,
    },
  },
  {
    name: "vendorShortName",
    label: "Short Name",
    type: "text",
    placeholder: "ABC",
    validation: {
      required: true,
      minLength: 3,
      maxLength: 100,
    },
  },
  {
    name: "address",
    label: "Address",
    type: "textarea",
    placeholder: "Mumbai",
    validation: {
      required: true,
    },
  },
  {
    name: "countryId",
    label: "Country",
    type: "cascading dropdown",
    placeholder: "Search country",
    parentField: null,
    optionMap: {},
    validation: {
      required: true,
    },
  },
  {
    name: "emailId",
    label: "Email",
    type: "text",
    placeholder: "contact@abcmetals.com",
    validation: {
      required: true,
      pattern: /^[^\s@]+@[^\s@]+\.[^\s@]+$/,
      patternMessage: "Enter a valid email address",
    },
  },
  {
    name: "mobileNo",
    label: "Mobile No",
    type: "text",
    placeholder: "9876543210",
    validation: {
      required: true,
      pattern: /^\d{10}$/,
      patternMessage: "Mobile No must be 10 digits",
      maxLength: 10,
      restrictTo: "digits",
    },
  },
  {
    name: "isActive",
    label: "Is Active",
    type: "checkbox",
    defaultValue: true,
  },
];

function VendorModulePage() {
  const navigate = useNavigate();
  const location = useLocation();
  const { id } = useEncryptedParams();
  const [countryOptions, setCountryOptions] = useState([]);
  const [editValues, setEditValues] = useState(null);
  const [isLoadingEdit, setIsLoadingEdit] = useState(false);
  const [formError, setFormError] = useState(null);

  const isCreatePage =
    location.pathname.endsWith("/new") || location.pathname.endsWith("/create");
  const isEditPage =
    Boolean(id) &&
    (location.pathname.endsWith("/edit") ||
      location.pathname.includes(`/${id}`));
  const isFormPage = isCreatePage || isEditPage;
  const formMode = isEditPage ? "edit" : "create";

  const basePath = useMemo(() => {
    if (location.pathname.includes("/settings-master/vendors"))
      return "/settings-master/vendors";
    if (location.pathname.includes("/master/vendors")) return "/master/vendors";
    return "/vendors";
  }, [location.pathname]);

  const fetchCountries = async () => {
    try {
      const countryItems = await countryService.getActiveCountries();
      const options = countryItems
        .map((country) => ({
          label: country?.countryName || country?.name,
          value: country?.countryId || country?.id,
        }))
        .filter((opt) => opt.label && opt.value);
      setCountryOptions(options);
    } catch {
      setCountryOptions([]);
    }
  };

  const { openModal, renderModals } = useMasterModals({
    country: fetchCountries,
  });

  useEffect(() => {
    fetchCountries();
  }, []);

  const endpoints = useMemo(
    () => ({
      list: "/vendor",
      create: "/vendor",
      edit: (id) => `/vendor/${id}`,
      update: (id) => `/vendor/${id}`,
      delete: (id) => `/vendor/${id}`,
    }),
    [],
  );

  const {
    rows,
    isLoading,
    isSubmitting,
    error,
    filters,
    pagination,
    sortConfig,
    askDelete,
    changePage,
    changeSort,
    updateFilters,
    refresh,
  } = useCrud({
    endpoints,
    idKey: "vendorId",
    updateMethod: "patch",
    initialFilters: { search: "" },
    initialSort: { key: "vendorName", order: "ASC" },
  });

  const vendorTableColumns = useMemo(
    () => [
      { key: "vendorName", label: "Vendor Name", sortable: true },
      { key: "vendorShortName", label: "Short Name", sortable: true },
      { key: "emailId", label: "Email", sortable: true },
      {
        key: "mobileNo",
        label: "Mobile No",
        sortable: true,
        render: (item) => item?.mobileNo || "-",
      },
      { key: "country.countryName", label: "Country", sortable: false },
      {
        key: "status",
        label: "Status",
        render: (item) => (
          <span
            className={`rounded-full px-2 py-1 text-xs font-semibold ${
              item?.isActive
                ? "bg-emerald-100 text-emerald-700 dark:bg-emerald-500/20 dark:text-emerald-300"
                : "bg-slate-100 text-slate-700 dark:bg-slate-700 dark:text-slate-300"
            }`}
          >
            {item?.isActive ? "Active" : "Inactive"}
          </span>
        ),
      },
    ],
    [],
  );

  const formSchema = useMemo(() => {
    return vendorFormSchema.map((field) => {
      if (field.name !== "countryId") return field;
      return {
        ...field,
        onAdd: () => openModal("country"),
        addLabel: "Add New Country",
        optionMap: {
          "": countryOptions,
        },
      };
    });
  }, [countryOptions, openModal]);

  useEffect(() => {
    if (!isEditPage || !id) {
      setEditValues(null);
      setFormError(null);
      return;
    }

    let isMounted = true;
    const loadEditVendor = async () => {
      setIsLoadingEdit(true);
      setFormError(null);
      try {
        const response = await apiClient.get(`/vendor/${id}`);
        const payload = response?.data || {};
        const party = payload?.data || payload || {};
        if (!isMounted) return;
        setEditValues(party);
      } catch (err) {
        if (!isMounted) return;
        const normalized = normalizeApiError(err);
        setFormError(normalized.message);
      } finally {
        if (isMounted) setIsLoadingEdit(false);
      }
    };

    loadEditVendor();
    return () => {
      isMounted = false;
    };
  }, [id, isEditPage]);

  const saveVendor = async (values) => {
    setFormError(null);
    try {
      if (formMode === "edit" && id) {
        await apiClient.patch(`/vendor/${id}`, values);
        alertService.success("Record updated successfully.");
      } else {
        await apiClient.post("/vendor", values);
        alertService.success("Record created successfully.");
      }
      await refresh();
      navigate(basePath, { replace: true });
    } catch (err) {
      const normalized = normalizeApiError(err);
      setFormError(normalized.message);
      alertService.error(normalized.message);
    }
  };

  const openCreate = () => navigate(`${basePath}/new`);
  const openEdit = (item) =>
    navigate(`${basePath}/${encrypt(item?.vendorId)}/edit`);

  if (isFormPage) {
    const notFound =
      formMode === "edit" && !isLoadingEdit && !editValues && !formError;

    return (
      <div className="space-y-3">
        <div className="flex justify-end">
          <Button variant="secondary" onClick={() => navigate(basePath)}>
            Back to List
          </Button>
        </div>

        {formError ? <ErrorAlert message={formError} /> : null}
        {notFound ? (
          <div className="rounded-xl border border-rose-200 bg-rose-50 px-4 py-3 text-sm text-rose-700">
            Record not found.
          </div>
        ) : (
          <div className="rounded-2xl border border-slate-200 bg-white p-4 dark:border-slate-800 dark:bg-slate-900">
            <CrudForm
              mode={formMode}
              schema={formSchema}
              initialValues={
                formMode === "edit" ? editValues || {} : { isActive: true }
              }
              isSubmitting={isSubmitting || isLoadingEdit}
              onSubmit={saveVendor}
              onCancel={() => navigate(basePath)}
            />
          </div>
        )}
        {renderModals()}
      </div>
    );
  }

  return (
    <div className="space-y-3">
      {error ? <ErrorAlert message={error} /> : null}

      <CrudTable
        columns={vendorTableColumns}
        rows={rows}
        isLoading={isLoading}
        pagination={pagination}
        sortConfig={sortConfig}
        filters={filters}
        onFiltersChange={updateFilters}
        onSort={changeSort}
        onPageChange={changePage}
        onEdit={openEdit}
        onDelete={askDelete}
        searchKey="search"
        searchPlaceholder="Search by vendor name, email, or mobile"
        tableScrollHeight="52vh"
        toolbarAction={<Button onClick={openCreate}>Create Vendor</Button>}
      />
      {renderModals()}
    </div>
  );
}

export default VendorModulePage;
